import React from "react";
import { ArrowUpDown } from "lucide-react";
import { LineupPill, StatusPill, na, pct } from "@/components/fbShared";

const COLS = [
  ["rank", "#"], ["name", "Player"], ["team", "Team"], ["opp", "Opp"], ["pos", "Pos"], ["starter", "Starter"],
  ["tip_edge", "Tip edge"], ["fb_rate", "FB rate"], ["weak_index", "Opp weak"], ["opening_signal", "1st-shot"],
  ["fusion", "Fusion"], ["prob", "Est. prob"], ["starts", "Starts"], ["status", "Status"],
];

const Sel = ({ value, onChange, options, testId, all }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
    className="h-8 rounded-md border border-slate-800 bg-slate-950 px-2 font-mono text-[11px] text-slate-200">
    <option value="">{all}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export const RankList = ({ title, note, rows, field, onPlayer, testId, fmt }) => (
  <div className="min-w-0 rounded-xl border border-slate-800 bg-[#0B0F17] p-3" data-testid={testId}>
    <p className="font-display text-[13px] font-bold uppercase text-orange-200">{title}</p>
    <p className="mb-2 font-mono text-[9px] text-slate-500">{note}</p>
    {rows.slice(0, 10).map((c, i) => (
      <button key={`${c.game_id}-${c.id}`} type="button" onClick={() => onPlayer(c.id)} data-testid={`${testId}-row-${i}`}
        className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left font-mono text-[11px] transition-colors hover:bg-slate-900">
        <span className="w-4 text-slate-600">{i + 1}</span>
        <span className="min-w-0 flex-1 truncate text-slate-100">{c.name} <span className="text-slate-500">{c.team} {c.pos} vs {c.opp}</span></span>
        <LineupPill s={c.starter} />
        <span className="w-14 text-right font-bold text-orange-300">{fmt(c[field])}</span>
      </button>
    ))}
    {!rows.length && <p className="font-mono text-[10px] text-slate-600">No candidates.</p>}
  </div>
);

export const PlayerBoard = ({ rows, games, onPlayer }) => {
  const [f, setF] = React.useState({ game: "", team: "", pos: "", arch: "", status: "", starter: "", minScore: "", minStarts: "" });
  const [sort, setSort] = React.useState({ k: "fusion", d: -1 });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const uniq = (fn) => [...new Set(rows.flatMap(fn).filter(Boolean))].sort();
  const view = rows
    .filter((c) => (!f.game || c.game_id === f.game) && (!f.team || c.team === f.team) && (!f.pos || c.pos === f.pos)
      && (!f.arch || c.archetypes.includes(f.arch)) && (!f.status || c.status === f.status)
      && (!f.starter || (f.starter === "CONFIRMED" ? c.starter === "CONFIRMED" : c.starter !== "CONFIRMED"))
      && (!f.minScore || (c.fusion || 0) >= Number(f.minScore)) && (!f.minStarts || c.starts >= Number(f.minStarts)))
    .sort((a, b) => {
      const x = a[sort.k], y = b[sort.k];
      if (x == null) return 1; if (y == null) return -1;
      return (x > y ? 1 : x < y ? -1 : 0) * sort.d;
    });
  const gameName = Object.fromEntries(games.map((g) => [g.game._id, `${g.game.away.abbr} @ ${g.game.home.abbr}`]));
  return (
    <div className="min-w-0 rounded-xl border border-slate-800 bg-[#0B0F17] p-3" data-testid="fb-board">
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <p className="mr-2 font-display text-[13px] font-bold uppercase text-orange-200">Player board</p>
        <select value={f.game} onChange={(e) => set("game")(e.target.value)} data-testid="fb-filter-game" className="h-8 rounded-md border border-slate-800 bg-slate-950 px-2 font-mono text-[11px] text-slate-200">
          <option value="">All games</option>{games.map((g) => <option key={g.game._id} value={g.game._id}>{gameName[g.game._id]}</option>)}
        </select>
        <Sel value={f.team} onChange={set("team")} options={uniq((c) => [c.team])} testId="fb-filter-team" all="All teams" />
        <Sel value={f.pos} onChange={set("pos")} options={uniq((c) => [c.pos])} testId="fb-filter-pos" all="All positions" />
        <Sel value={f.arch} onChange={set("arch")} options={uniq((c) => c.archetypes)} testId="fb-filter-arch" all="All archetypes" />
        <Sel value={f.status} onChange={set("status")} options={uniq((c) => [c.status])} testId="fb-filter-status" all="All statuses" />
        <Sel value={f.starter} onChange={set("starter")} options={["CONFIRMED", "PROJECTED/EXPECTED"]} testId="fb-filter-starter" all="All starters" />
        <input value={f.minScore} onChange={(e) => set("minScore")(e.target.value)} placeholder="Min fusion" data-testid="fb-filter-score" className="h-8 w-24 rounded-md border border-slate-800 bg-slate-950 px-2 font-mono text-[11px] text-slate-200" />
        <input value={f.minStarts} onChange={(e) => set("minStarts")(e.target.value)} placeholder="Min starts" data-testid="fb-filter-starts" className="h-8 w-24 rounded-md border border-slate-800 bg-slate-950 px-2 font-mono text-[11px] text-slate-200" />
        <span className="ml-auto font-mono text-[10px] text-slate-500" data-testid="fb-board-count">{view.length} players</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1000px] font-mono text-[11px]">
          <thead><tr className="border-b border-slate-800 text-left text-[9px] uppercase tracking-wider text-slate-500">
            {COLS.map(([k, l]) => (
              <th key={k} className="px-1.5 py-1.5">
                {k === "rank" ? l : <button type="button" data-testid={`fb-sort-${k}`} onClick={() => setSort((s) => ({ k, d: s.k === k ? -s.d : -1 }))} className="flex items-center gap-0.5 hover:text-orange-300">{l}<ArrowUpDown className="h-2.5 w-2.5" /></button>}
              </th>
            ))}
          </tr></thead>
          <tbody>
            {view.map((c, i) => (
              <tr key={`${c.game_id}-${c.id}`} onClick={() => onPlayer(c.id)} data-testid={`fb-board-row-${c.id}`} className="cursor-pointer border-b border-slate-900 transition-colors hover:bg-slate-900/70">
                <td className="px-1.5 py-1.5 text-slate-600">{i + 1}</td>
                <td className="px-1.5 font-bold text-slate-100">{c.name}{c.jumper && <span className="ml-1 rounded bg-orange-500/15 px-1 text-[8px] text-orange-300">JUMPER</span>}</td>
                <td className="px-1.5 text-slate-300">{c.team}</td><td className="px-1.5 text-slate-400">{c.opp}</td>
                <td className="px-1.5 text-slate-300">{c.pos || "N/A"}</td><td className="px-1.5"><LineupPill s={c.starter} /></td>
                <td className="px-1.5 text-slate-300">{pct(c.tip_edge, 0)}</td><td className="px-1.5 text-slate-300">{na(c.fb_rate, "%")}</td>
                <td className="px-1.5 text-slate-300">{na(c.weak_index, "x")}</td><td className="px-1.5 text-slate-300">{pct(c.opening_signal, 0)}</td>
                <td className="px-1.5 font-bold text-orange-300">{na(c.fusion)}</td>
                <td className="px-1.5 text-slate-300">{c.prob_calibrated ? pct(c.prob) : "—"}</td>
                <td className="px-1.5 text-slate-400">{c.starts}</td><td className="px-1.5"><StatusPill s={c.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
